/**
 * Configuración del cliente (build-time). El SPA es 100% front y se alimenta
 * 100% de BridgeHub: aquí vive la ÚNICA referencia a su URL base.
 *
 *  - dev:  `VITE_BRIDGE_URL` ausente → `'/bridge'` (proxy de Vite → localhost:8000).
 *  - prod (deploy estático S3/CloudFront): `VITE_BRIDGE_URL` = dominio de BridgeHub
 *    (con CORS habilitado). Se inyecta al `vite build`.
 */
export const BRIDGE_URL = (import.meta.env.VITE_BRIDGE_URL ?? '').replace(/\/+$/, '') || '/bridge';

/**
 * Modo del dashboard. El MISMO build (un solo deploy en S3/CloudFront) sirve los
 * dos dashboards; el modo se resuelve en runtime, en este orden:
 *
 *  1. URL: `…/#/logistica` o `…?mode=logistica` → logística.
 *     Se usa el HASH (`#/logistica`) a propósito: el hash NO llega al servidor,
 *     así CloudFront sirve el mismo index.html sin rutas extra (cero cambios en
 *     S3/CloudFront). Una ruta real `/logistica` daría 404 en el CDN.
 *  2. Build: `VITE_DASHBOARD_MODE=logistica` → logística (para un deploy dedicado
 *     futuro, p. ej. `logistica.dashboard.*`; sigue soportado).
 *  3. Default → ventas/comercio.
 *
 * Cada modo usa su propio namespace de storage (ver dashboardContext) → los
 * tableros de comercio y logística NUNCA se mezclan, aunque compartan dominio.
 */
export type DashboardMode = 'sales' | 'logistica' | 'compras';

function resolveDashboardMode(): DashboardMode {
  if (typeof window !== 'undefined') {
    const url = `${window.location.hash} ${window.location.search}`.toLowerCase();
    if (url.includes('logistica')) return 'logistica'; // #/logistica · ?mode=logistica
    if (url.includes('compras')) return 'compras'; // #/compras · ?mode=compras
  }
  const build = import.meta.env.VITE_DASHBOARD_MODE;
  if (build === 'logistica') return 'logistica';
  if (build === 'compras') return 'compras';
  return 'sales';
}

export const DASHBOARD_MODE: DashboardMode = resolveDashboardMode();

/**
 * URL base del Odoo de VG/Pielé (comparten instancia) para deep-links a la orden
 * desde el drill-down — acceso rápido al backoffice. Es solo un enlace de
 * conveniencia (el usuario abre Odoo aparte); NO se consume data de Odoo aquí.
 */
export const ODOO_WEB_URL = (import.meta.env.VITE_ODOO_WEB_URL ?? 'https://salogaash.mx').replace(/\/+$/, '');

/** Odoo de SmartBamboo (instancia distinta) para deep-links de sus órdenes. */
export const ODOO_SB_WEB_URL = (import.meta.env.VITE_ODOO_SB_WEB_URL ?? 'https://odoo-smartbamboo.mx').replace(/\/+$/, '');
